/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {calendarDate} from '@ion/core/src/utils/DateUtil';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import {DLSTheme} from '@ion/dls/src/types/types';
import {resolveValue} from '@ion/dls/src/utils/ComponentUtils';

interface AssetAdditionalInfoContentProps {
  asset: Device;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    content : {
      padding : '0 15px'
    }
  };
});

const AssetAdditionalInfoContent = ({
  asset: currentAsset
}: AssetAdditionalInfoContentProps) => {
  const classes = useStyles();
  const asset = currentAsset instanceof Device ? currentAsset : new Device(currentAsset);
  const {
    name,
    description,
    type,
    category,
    installDate,
    licence,
    state,
    year,
    make,
    model,
    vin,
    engineType,
    deviceId,
    deviceType,
    esn,
    deviceStatus
  } = translate('tracking:assetDetail.additionalInfo', {returnObjects: true});

  const date          = asset?.getAssetValue('installDate');
  const itemEsn       = asset?.getItemValue({deviceAttribute: 'deviceEsn', cvsAttribute: 'deviceEsn'});
  const itemName      = asset?.getItemValue({deviceAttribute: 'assetName', cvsAttribute: 'name'});
  const itemDate      = calendarDate(date);
  const fuelType      = asset.getCommonDeviceAttribute('fuelType');
  const itemStatus    = asset?.getItemValue({deviceAttribute: 'status', cvsAttribute: 'device.status'});
  const itemDeviceId  = asset?.getItemValue({deviceAttribute: 'deviceIdValue', cvsAttribute: 'device.id'});
  const fuelTypeValue = !isNil(fuelType) ? translate(`tracking:engineType.${fuelType}`) : null;

  const fields = [
    {
      label : name,
      value : itemName
    },
    {
      label : description,
      value : asset.getItemValue({deviceAttribute: 'description', cvsAttribute: 'description'})
    },
    {
      label : type,
      value : asset.getCommonDeviceAttribute('assetType')
    },
    {
      label : category,
      value : asset.getCommonDeviceAttribute('assetCategory')
    },
    {
      label : installDate,
      value : itemDate
    },
    {
      label : licence,
      value : asset?.getAssetValue('licenseNumber')
    },
    {
      label : state,
      value : asset.getCommonDeviceAttribute('state')
    },
    {
      label : year,
      value : asset.getCommonDeviceAttribute('year')
    },
    {
      label : make,
      value : asset.getCommonDeviceAttribute('make')
    },
    {
      label : model,
      value : asset.getCommonDeviceAttribute('model')
    },
    {
      label : vin,
      value : asset.getCommonDeviceAttribute('vin')
    },
    {
      label : engineType,
      value : fuelTypeValue
    },
    {
      label : deviceId,
      value : itemDeviceId?.toString()
    },
    {
      label : deviceType,
      value : asset?.getItemValue({deviceAttribute: 'device.type.title', cvsAttribute: 'device.type.title'})
    },
    {
      label : esn,
      value : itemEsn
    },
    {
      label : deviceStatus,
      value : itemStatus
    }
  ];

  return (
    <div className={classes.content}>
      {!isNil(asset) && fields.map(({label, value}) => (
        <CardItemRow key={label}
          value={resolveValue(value)}
          label={label}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />
      ))}
    </div>
  );
};

export default memo(AssetAdditionalInfoContent);
